import { createHash } from "node:crypto";
import type { ProposalAction } from "./proposer.js";

/**
 * Input to {@link computeFingerprint}. Two shapes — one per surface — share
 * a single function so the decline store can carry both kinds of
 * fingerprints in the same lookup set.
 *
 * The action-aware fingerprint is the load-bearing design choice: declining
 * an `append-section` proposal for topic X does *not* prevent a later
 * `create-file` proposal for the same topic from surfacing. The LLM's
 * placement decision is part of the fingerprint, so a shifted decision is
 * a new proposal.
 */
export type FingerprintInput =
  | {
      readonly kind: "capture-insight";
      readonly turnIds: readonly string[];
      readonly topic: string;
      readonly actionKind: ProposalAction["kind"];
      readonly targetPath: string;
    }
  | {
      readonly kind: "create-hub";
      readonly topic: string;
      readonly sourceDocs: readonly string[];
      readonly actionKind: ProposalAction["kind"];
      readonly targetPath: string;
    };

/**
 * Compute a stable 16-hex-char fingerprint for a proposal. Stable means:
 * identical logical input — regardless of source ordering of array fields or
 * path separator style — produces an identical fingerprint, so the decline
 * lookup is reliable across machines (work / home) and across path-separator
 * regimes (Windows backslash vs POSIX slash).
 *
 * SHA-256 truncated to 16 hex chars (64 bits) is enough collision resistance
 * for a per-instance decline set; the full hash adds storage with no
 * practical benefit at this scale.
 */
export function computeFingerprint(input: FingerprintInput): string {
  const normalized = normalizeForHash(input);
  return createHash("sha256").update(normalized).digest("hex").slice(0, 16);
}

function normalizeForHash(input: FingerprintInput): string {
  if (input.kind === "capture-insight") {
    return JSON.stringify({
      kind: "capture-insight",
      turnIds: [...input.turnIds].sort(),
      topic: normalizeTopic(input.topic),
      actionKind: input.actionKind,
      targetPath: normalizePath(input.targetPath),
    });
  }
  return JSON.stringify({
    kind: "create-hub",
    topic: normalizeTopic(input.topic),
    sourceDocs: [...input.sourceDocs].map(normalizePath).sort(),
    actionKind: input.actionKind,
    targetPath: normalizePath(input.targetPath),
  });
}

function normalizeTopic(t: string): string {
  return t.trim().toLowerCase();
}

function normalizePath(p: string): string {
  return p.replace(/\\/g, "/");
}
